import { useState, useEffect, useCallback, useRef } from 'react';
import { mergeUsageDetailsData } from '../../../utils/usage-merge';
import {
  DAILY_COST_MONITOR_COMPLETION_VERSION,
  subscribeDailyCostMonitorCompletion,
  type DailyCostMonitorCompletionPayload,
  type DailyCostMonitorCompletionSignal
} from '../../../utils/daily-cost-monitor-events';
import { formatUtcDateKey } from '../../../utils/daily-user-budget';
import {
  getApiKey,
  getDevSettings,
  readAdminCredentialRevision,
  removeCache
} from '../../../utils/storage';
import { openaiCache } from '../../../utils/cache-service';
import { getOrganizationUsageDetails, UsageDetailsData } from '../../../utils/openai-api';
import { cleanupOrphanedMetadata, getApiKeyMetadata, initializeNewApiKeyMetadata } from '@/utils/label-budget-storage';
import { getAnthropicUsageDetails } from '@/utils/anthropic-usage-adapter';
import { UsageDataSource } from '../types';
import type { ApiKeyMetadata, LabelDefinition } from '../../../types/label-budget';
import { debugLog } from '../../../utils/debug-log';

export interface UseFetchUsageParams {
  dataSource: UsageDataSource;
  resolvedHasOpenAiKey: boolean;
  resolvedHasAnthropicKey: boolean;
  credentialRevision?: string;
  getDateRange: () => { startDate: Date; endDate: Date; label: string };
  dateRange: string;
  customDateRange: { startDate: Date; endDate: Date } | null;
  setLabelDefinitions: (defs: LabelDefinition[]) => void;
  setApiKeyMetadata: (meta: Record<string, Partial<ApiKeyMetadata>>) => void;
  setPageSize: (size: number | ((prev: number) => number)) => void;
}

export interface UseFetchUsageReturn {
  data: UsageDetailsData | null;
  setData: React.Dispatch<React.SetStateAction<UsageDetailsData | null>>;
  loading: boolean;
  isReloading: boolean;
  error: string | null;
  progress: string;
  isBackgroundLoading: boolean;
  showLoadingModal: boolean;
  setShowLoadingModal: React.Dispatch<React.SetStateAction<boolean>>;
  loadingProgress: string;
  setIsBackgroundLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchUsageData: (clearCache?: boolean, isBackground?: boolean) => Promise<void>;
}

export const selectMonitorDataForDashboard = (
  providers: Partial<Record<'openai' | 'anthropic', UsageDetailsData>> | undefined,
  dataSource: 'openai' | 'anthropic' | 'all',
  dateRange: string,
  customDateRange: { startDate: Date; endDate: Date } | null = null
): UsageDetailsData | undefined => {
  if (!providers || dateRange !== '1day' || customDateRange) return undefined;
  if (dataSource === 'openai') return providers.openai;
  if (dataSource === 'anthropic') return providers.anthropic;
  if (providers.openai && providers.anthropic) {
    return mergeUsageDetailsData([providers.openai, providers.anthropic]);
  }
  return providers.openai ?? providers.anthropic;
};

export const isCompleteCurrentMonitorPayload = (
  payload: DailyCostMonitorCompletionPayload | null | undefined,
  requiredProviders: { openai: boolean; anthropic: boolean },
  now: Date = new Date(),
  expectedCredentialRevision: string = readAdminCredentialRevision()
): payload is DailyCostMonitorCompletionPayload => {
  if (
    !payload
    || payload.type !== 'complete'
    || payload.version !== DAILY_COST_MONITOR_COMPLETION_VERSION
    || typeof payload.utcDay !== 'string'
    || !Number.isFinite(payload.completedAt)
    || payload.credentialRevision !== expectedCredentialRevision
    || !payload.providers
  ) return false;

  const completedAt = new Date(payload.completedAt);
  if (
    formatUtcDateKey(now) !== payload.utcDay
    || formatUtcDateKey(completedAt) !== payload.utcDay
  ) return false;

  const required = (['openai', 'anthropic'] as const).filter(provider => requiredProviders[provider]);
  return required.every(provider => {
    const data = payload.providers[provider];
    return !!data && !data.partialErrors?.length;
  });
};

export const isCurrentMonitorSignal = (
  signal: DailyCostMonitorCompletionSignal | null | undefined,
  now: Date = new Date(),
  expectedCredentialRevision: string = readAdminCredentialRevision()
): signal is DailyCostMonitorCompletionSignal => {
  if (
    !signal
    || signal.type !== 'complete-signal'
    || signal.version !== DAILY_COST_MONITOR_COMPLETION_VERSION
    || typeof signal.utcDay !== 'string'
    || !Number.isFinite(signal.completedAt)
    || signal.credentialRevision !== expectedCredentialRevision
  ) return false;
  return formatUtcDateKey(now) === signal.utcDay
    && formatUtcDateKey(new Date(signal.completedAt)) === signal.utcDay;
};

interface CacheDateKey {
  start: string;
  end: string;
}

type SegmentBucketWidth = '1m' | '1h' | '1d';

interface SegmentCacheClient {
  getUsageSegments: (
    bucketWidth: SegmentBucketWidth,
    startTime: number,
    endTime: number
  ) => Promise<Array<{ startTime: number; endTime: number; data: unknown }>>;
  removeUsageSegment: (
    bucketWidth: SegmentBucketWidth,
    startTime: number,
    endTime: number
  ) => Promise<boolean>;
}

export const getOpenAiUsageCacheDateKeys = (startDate: Date, endDate: Date): CacheDateKey[] => {
  const rangeMs = Math.max(0, endDate.getTime() - startDate.getTime());
  const hasHourlyPrecision = rangeMs < 24 * 60 * 60 * 1000;
  const startDateIso = startDate.toISOString();
  const endDateIso = endDate.toISOString();
  const startDateOnly = startDateIso.split('T')[0];
  const endDateOnly = endDateIso.split('T')[0];

  const keyCandidates: CacheDateKey[] = [
    {
      start: hasHourlyPrecision ? startDateIso : startDateOnly,
      end: hasHourlyPrecision ? endDateIso : endDateOnly
    },
    {
      start: startDateOnly,
      end: endDateOnly
    }
  ];

  return Array.from(
    new Map(keyCandidates.map(key => [`${key.start}|${key.end}`, key])).values()
  );
};

const DAY_SECONDS = 24 * 60 * 60;

const getTodayStartUtcSec = (): number => {
  const nowSec = Math.floor(Date.now() / 1000);
  return Math.floor(nowSec / DAY_SECONDS) * DAY_SECONDS;
};

export const clearMutableSegmentsForRange = async (
  cacheClient: SegmentCacheClient,
  bucketWidths: SegmentBucketWidth[],
  startDate: Date,
  endDate: Date,
  finalizationBoundarySec: number = getTodayStartUtcSec()
): Promise<number> => {
  const startSec = Math.floor(startDate.getTime() / 1000);
  const endSec = Math.ceil(endDate.getTime() / 1000);
  let clearedCount = 0;

  for (const bucketWidth of bucketWidths) {
    const segments = await cacheClient.getUsageSegments(bucketWidth, startSec, endSec);
    for (const segment of segments) {
      if (!Number.isFinite(segment.startTime) || !Number.isFinite(segment.endTime)) continue;
      // Only invalidate mutable (non-finalized) segments to keep historical cache reuse.
      if (segment.endTime <= finalizationBoundarySec) continue;
      const removed = await cacheClient.removeUsageSegment(bucketWidth, segment.startTime, segment.endTime);
      if (removed) {
        clearedCount += 1;
      }
    }
  }

  return clearedCount;
};

export function useFetchUsage({
  dataSource,
  resolvedHasOpenAiKey,
  resolvedHasAnthropicKey,
  credentialRevision,
  getDateRange,
  dateRange,
  customDateRange,
  setApiKeyMetadata,
  setPageSize,
}: UseFetchUsageParams): UseFetchUsageReturn {
  const [data, setData] = useState<UsageDetailsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');
  const backgroundModalCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSequenceRef = useRef(0);
  const activeRequestIdRef = useRef(0);
  const requestInFlightCountRef = useRef(0);
  const monitorRefreshPromiseRef = useRef<Promise<void> | null>(null);
  const lastMonitorCompletionRef = useRef(0);
  const pendingMonitorRefreshRef = useRef<{
    completedAt: number;
    credentialRevision: string;
  } | null>(null);
  const drainPendingMonitorRefreshRef = useRef<() => void>(() => {});
  const pendingMonitorPayloadRef = useRef<{
    completedAt: number;
    credentialRevision: string;
    data: UsageDetailsData;
    openAiData?: UsageDetailsData;
  } | null>(null);

  const syncOpenAiKeyMetadata = useCallback((openAiData?: UsageDetailsData): void => {
    if (!openAiData?.apiKeyUsage.length) return;
    const existingMetadata = getApiKeyMetadata();
    const metadataSet = new Set(existingMetadata.map(meta => meta.apiKeyId));
    const activeApiKeyIds = openAiData.apiKeyUsage.map(key => key.id);

    openAiData.apiKeyUsage.forEach(apiKey => {
      if (!metadataSet.has(apiKey.id)) {
        initializeNewApiKeyMetadata(apiKey.id, apiKey.name);
        metadataSet.add(apiKey.id);
      }
    });

    cleanupOrphanedMetadata(activeApiKeyIds);
    const metadataMap: Record<string, Partial<ApiKeyMetadata>> = {};
    getApiKeyMetadata().forEach(meta => {
      metadataMap[meta.apiKeyId] = meta;
    });
    setApiKeyMetadata(metadataMap);
  }, [setApiKeyMetadata]);

  // Data fetching
  const performFetchUsageData = useCallback(async (clearCache = false, isBackground = false) => {
    const requestId = ++requestSequenceRef.current;
    activeRequestIdRef.current = requestId;
    const requestCredentialRevision = credentialRevision ?? readAdminCredentialRevision();
    const isCurrentRequest = () => activeRequestIdRef.current === requestId
      && requestCredentialRevision === readAdminCredentialRevision();
    const failEarly = (message: string) => {
      if (!isCurrentRequest()) return;
      setError(message);
      setData(null);
      setLoading(false);
      setIsReloading(false);
      setIsBackgroundLoading(false);
      setShowLoadingModal(false);
      setLoadingProgress('');
      setProgress('');
    };

    if (backgroundModalCloseTimerRef.current) {
      clearTimeout(backgroundModalCloseTimerRef.current);
      backgroundModalCloseTimerRef.current = null;
    }

    const wantsOpenAI = (dataSource === 'openai' || dataSource === 'all') && resolvedHasOpenAiKey;
    const wantsAnthropic = (dataSource === 'anthropic' || dataSource === 'all') && resolvedHasAnthropicKey;

    if (!wantsOpenAI && dataSource === 'openai') {
      failEarly('OpenAI Admin API key is not configured');
      return;
    }
    if (!wantsAnthropic && dataSource === 'anthropic') {
      failEarly('Anthropic Admin API key is not configured');
      return;
    }
    if (!wantsOpenAI && !wantsAnthropic) {
      failEarly('No available data sources. Please configure an Admin API key.');
      return;
    }

    if (clearCache && wantsOpenAI) {
      const { startDate, endDate } = getDateRange();
      const uniqueCacheKeys = getOpenAiUsageCacheDateKeys(startDate, endDate);

      for (const cacheKey of uniqueCacheKeys) {
        await openaiCache.clearOrganizationUsageCache(cacheKey.start, cacheKey.end);
        const legacyKey = `${cacheKey.start}_${cacheKey.end}`;
        removeCache(`organization_usage_details_${legacyKey}`);
      }

      debugLog('Cache cleared for date range keys:', uniqueCacheKeys);
      debugLog('OpenAI historical segments retained; explicit refresh will reload metadata, recent usage and costs');
    }

    if (clearCache && wantsAnthropic) {
      debugLog('Anthropic usage segments retained; incremental fetch will request only missing or expired ranges');
    }

    const currentDateRange = getDateRange();
    debugLog('🔍 Current date range in UsageDashboard:', {
      source: dataSource,
      calculatedRange: currentDateRange,
      startDate: currentDateRange.startDate.toISOString(),
      endDate: currentDateRange.endDate.toISOString(),
      label: currentDateRange.label
    });

    const shouldStreamPartialResults = !isBackground && !clearCache;

    if (isBackground) {
      setIsBackgroundLoading(true);
      setShowLoadingModal(true);
    } else if (!isBackground && clearCache) {
      setIsReloading(true);
    } else {
      setLoading(true);
      setProgress('');
      setData(null);
    }
    setError(null);

    try {
      const { startDate, endDate, label } = currentDateRange;
      const results: UsageDetailsData[] = [];
      let openAiData: UsageDetailsData | null = null;
      const publishResults = () => {
        if (!isCurrentRequest()) return;
        if (results.length === 0) return;
        if (results.length === 1) {
          setData(results[0]);
          return;
        }
        setData(mergeUsageDetailsData(results));
      };

      if (wantsOpenAI) {
        const apiKey = getApiKey();
        if (!apiKey) {
          throw new Error('OpenAI Admin API key is not configured');
        }
        const openAIResponse = await getOrganizationUsageDetails(apiKey, (message) => {
          if (!isCurrentRequest()) return;
          const prefixed = dataSource === 'all' ? `[OpenAI] ${message}` : message;
          if (isBackground) {
            setLoadingProgress(prefixed);
          } else {
            setProgress(prefixed);
          }
        }, { startDate, endDate, label }, true, requestCredentialRevision, { forceRefresh: clearCache });

        if (!openAIResponse.success) {
          throw new Error(openAIResponse.error || 'Failed to fetch OpenAI usage data');
        }
        if (!isCurrentRequest()) return;
        if (openAIResponse.data) {
          results.push(openAIResponse.data);
          openAiData = openAIResponse.data;
          if (shouldStreamPartialResults) {
            publishResults();
          }
        }
      }

      if (wantsAnthropic) {
        const anthropicResponse = await getAnthropicUsageDetails(
          { startDate, endDate, label },
          (message) => {
            if (!isCurrentRequest()) return;
            const prefixed = dataSource === 'all' ? `[Anthropic] ${message}` : message;
            if (isBackground) {
              setLoadingProgress(prefixed);
            } else {
              setProgress(prefixed);
            }
          }
        );

        if (!anthropicResponse.success) {
          throw new Error(anthropicResponse.error || 'Failed to fetch Anthropic usage data');
        }
        if (!isCurrentRequest()) return;
        if (anthropicResponse.data) {
          results.push(anthropicResponse.data);
          if (shouldStreamPartialResults) {
            publishResults();
          }
        }
      }

      if (!isCurrentRequest()) return;
      if (results.length === 0) {
        setData(null);
      } else {
        publishResults();
      }

      syncOpenAiKeyMetadata(openAiData ?? undefined);
    } catch (err) {
      if (!isCurrentRequest()) return;
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      console.error('Usage data fetch error:', err);
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
        setIsReloading(false);
        setIsBackgroundLoading(false);
        if (isBackground) {
          backgroundModalCloseTimerRef.current = setTimeout(() => {
            setShowLoadingModal(false);
            setLoadingProgress('');
            backgroundModalCloseTimerRef.current = null;
          }, 1000);
        }
      }
    }
  }, [
    credentialRevision,
    dataSource,
    getDateRange,
    resolvedHasAnthropicKey,
    resolvedHasOpenAiKey,
    syncOpenAiKeyMetadata
  ]);

  const fetchUsageData = useCallback(async (
    clearCache = false,
    isBackground = false
  ): Promise<void> => {
    requestInFlightCountRef.current += 1;
    try {
      await performFetchUsageData(clearCache, isBackground);
    } finally {
      requestInFlightCountRef.current = Math.max(0, requestInFlightCountRef.current - 1);
      if (requestInFlightCountRef.current === 0 && pendingMonitorPayloadRef.current) {
        const pending = pendingMonitorPayloadRef.current;
        pendingMonitorPayloadRef.current = null;
        if (pending.credentialRevision === readAdminCredentialRevision()) {
          syncOpenAiKeyMetadata(pending.openAiData);
          setData(pending.data);
          setError(null);
        }
      }
      if (requestInFlightCountRef.current === 0) {
        drainPendingMonitorRefreshRef.current();
      }
    }
  }, [performFetchUsageData, syncOpenAiKeyMetadata]);

  // Fetch on mount and whenever fetch parameters change
  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  // Reuse complete Today data in every tab. BroadcastChannel transports the
  // structured payload; the storage fallback carries only a completion signal
  // and performs one cache-backed refresh.
  useEffect(() => {
    const requiredProviders = {
      openai: resolvedHasOpenAiKey,
      anthropic: resolvedHasAnthropicKey
    };
    const isFixedRange = customDateRange !== null || dateRange === 'yesterday';
    let fallbackSignalTimer: number | null = null;
    let pendingFallbackSignal: DailyCostMonitorCompletionSignal | null = null;

    const startBackgroundRefresh = (
      completedAt: number,
      eventCredentialRevision: string
    ): void => {
      if (
        isFixedRange
        || eventCredentialRevision !== readAdminCredentialRevision()
      ) return;
      if (requestInFlightCountRef.current > 0 || monitorRefreshPromiseRef.current) {
        const pending = pendingMonitorRefreshRef.current;
        if (!pending || completedAt > pending.completedAt) {
          pendingMonitorRefreshRef.current = {
            completedAt,
            credentialRevision: eventCredentialRevision
          };
        }
        return;
      }

      pendingMonitorRefreshRef.current = null;
      lastMonitorCompletionRef.current = completedAt;
      const refresh = fetchUsageData(false, true);
      monitorRefreshPromiseRef.current = refresh;
      const clearRefresh = () => {
        if (monitorRefreshPromiseRef.current === refresh) {
          monitorRefreshPromiseRef.current = null;
        }
        drainPendingMonitorRefreshRef.current();
      };
      void refresh.then(clearRefresh, clearRefresh);
    };

    drainPendingMonitorRefreshRef.current = () => {
      const pending = pendingMonitorRefreshRef.current;
      if (!pending) return;
      startBackgroundRefresh(pending.completedAt, pending.credentialRevision);
    };

    const handlePayload = (payload: DailyCostMonitorCompletionPayload) => {
      if (
        pendingFallbackSignal?.completedAt === payload.completedAt
        && pendingFallbackSignal.credentialRevision === payload.credentialRevision
      ) {
        if (fallbackSignalTimer) window.clearTimeout(fallbackSignalTimer);
        fallbackSignalTimer = null;
        pendingFallbackSignal = null;
        const pendingRefresh = pendingMonitorRefreshRef.current;
        if (
          pendingRefresh?.completedAt === payload.completedAt
          && pendingRefresh.credentialRevision === payload.credentialRevision
        ) {
          pendingMonitorRefreshRef.current = null;
        }
      }
      if (
        !isCompleteCurrentMonitorPayload(payload, requiredProviders)
        || payload.completedAt <= lastMonitorCompletionRef.current
      ) return;

      const nextData = selectMonitorDataForDashboard(
        payload.providers,
        dataSource,
        dateRange,
        customDateRange
      );
      if (nextData) {
        lastMonitorCompletionRef.current = payload.completedAt;
        if (requestInFlightCountRef.current > 0) {
          const pending = pendingMonitorPayloadRef.current;
          if (!pending || payload.completedAt > pending.completedAt) {
            pendingMonitorPayloadRef.current = {
              completedAt: payload.completedAt,
              credentialRevision: payload.credentialRevision,
              data: nextData,
              openAiData: payload.providers.openai
            };
          }
          return;
        }
        syncOpenAiKeyMetadata(payload.providers.openai);
        setData(nextData);
        setError(null);
        return;
      }
      startBackgroundRefresh(payload.completedAt, payload.credentialRevision);
    };

    const handleSignal = (signal: DailyCostMonitorCompletionSignal) => {
      if (
        !isCurrentMonitorSignal(signal)
        || signal.completedAt <= lastMonitorCompletionRef.current
      ) return;
      pendingFallbackSignal = signal;
      if (fallbackSignalTimer) window.clearTimeout(fallbackSignalTimer);
      fallbackSignalTimer = window.setTimeout(() => {
        fallbackSignalTimer = null;
        const pending = pendingFallbackSignal;
        pendingFallbackSignal = null;
        if (pending) {
          startBackgroundRefresh(pending.completedAt, pending.credentialRevision);
        }
      }, 50);
    };

    const unsubscribe = subscribeDailyCostMonitorCompletion({
      onPayload: handlePayload,
      onSignal: handleSignal
    });
    return () => {
      unsubscribe();
      if (fallbackSignalTimer) window.clearTimeout(fallbackSignalTimer);
      fallbackSignalTimer = null;
      pendingFallbackSignal = null;
      pendingMonitorPayloadRef.current = null;
      pendingMonitorRefreshRef.current = null;
      drainPendingMonitorRefreshRef.current = () => {};
    };
  }, [
    customDateRange,
    dataSource,
    dateRange,
    fetchUsageData,
    credentialRevision,
    resolvedHasAnthropicKey,
    resolvedHasOpenAiKey,
    syncOpenAiKeyMetadata
  ]);

  // Monitor settings changes and re-fetch data
  useEffect(() => {
    const handleSettingsChange = () => {
      fetchUsageData(true);

      const devSettings = getDevSettings();
      const limit = devSettings.apiKeyLimit;
      setPageSize(limit === 'unlimited' ? 0 : (limit || 100));
    };

    window.addEventListener('devSettingsChanged', handleSettingsChange);
    return () => window.removeEventListener('devSettingsChanged', handleSettingsChange);
  }, [fetchUsageData, setPageSize]);

  useEffect(() => {
    return () => {
      if (backgroundModalCloseTimerRef.current) {
        clearTimeout(backgroundModalCloseTimerRef.current);
        backgroundModalCloseTimerRef.current = null;
      }
    };
  }, []);

  return {
    data,
    setData,
    loading,
    isReloading,
    error,
    progress,
    isBackgroundLoading,
    showLoadingModal,
    setShowLoadingModal,
    loadingProgress,
    setIsBackgroundLoading,
    fetchUsageData,
  };
}
